import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';

/**
 * Tipo de cita que se puede seleccionar al programar desde órdenes de servicio.
 *
 * - ADMIN: Cita administrativa (sin vínculo a orden de servicio)
 * - SERVICIO: Cita Campo/Informe (vinculada a la orden de servicio actual)
 */
export type UsoCitaTipo = 'ADMIN' | 'SERVICIO';

interface OpcionTipoCita {
  label: string;
  value: UsoCitaTipo;
  description: string;
}

/**
 * TipoCitaSelectorDialogComponent
 *
 * Diálogo que permite seleccionar el tipo de cita a programar cuando se hace click
 * en un espacio vacío de la agenda desde "Programar Servicio".
 *
 * Replica el comportamiento del legacy donde se pregunta:
 * - Administrativas: Cita sin vínculo a orden de servicio
 * - Campo/Informe: Cita vinculada a la orden de servicio actual
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-tipo-cita-selector-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule
  ],
  template: `
    <p-dialog
      [(visible)]="dialogVisible"
      header="Tipo de Cita"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '28rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      [closable]="true"
      [closeOnEscape]="true"
      (onHide)="onCancelar()"
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm text-gray-600">
          Seleccione el tipo de cita que desea programar:
        </p>

        <p-dropdown
          [(ngModel)]="tipoSeleccionado"
          [options]="opciones"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleccione una opción"
          [style]="{ width: '100%' }"
          appendTo="body"
        >
          <ng-template pTemplate="item" let-option>
            <div class="flex flex-col">
              <span class="font-medium">{{ option.label }}</span>
              <span class="text-xs text-gray-500">{{
                option.description
              }}</span>
            </div>
          </ng-template>
        </p-dropdown>
      </div>

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
          <p-button
            label="Aceptar"
            icon="pi pi-check"
            [disabled]="!tipoSeleccionado"
            styleClass="min-h-[44px]"
            (onClick)="onAceptar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class TipoCitaSelectorDialogComponent {
  // === Inputs ===
  visible = input.required<boolean>();

  // === Outputs ===
  seleccionado = output<UsoCitaTipo>();
  cancelado = output<void>();

  // === State ===
  dialogVisible = false;
  tipoSeleccionado: UsoCitaTipo | null = null;

  opciones: OpcionTipoCita[] = [
    {
      label: 'Administrativas',
      value: 'ADMIN',
      description: 'Cita sin vínculo a orden de servicio'
    },
    {
      label: 'Campo/Informe',
      value: 'SERVICIO',
      description: 'Cita vinculada a la orden de servicio actual'
    }
  ];

  constructor() {
    // Effect no necesario aquí, manejamos visible en ngOnChanges o input effect
  }

  ngOnChanges(): void {
    this.dialogVisible = this.visible();
    if (this.dialogVisible) {
      // Reset selection when dialog opens
      this.tipoSeleccionado = null;
    }
  }

  onAceptar(): void {
    if (this.tipoSeleccionado) {
      this.dialogVisible = false;
      this.seleccionado.emit(this.tipoSeleccionado);
    }
  }

  onCancelar(): void {
    this.dialogVisible = false;
    this.tipoSeleccionado = null;
    this.cancelado.emit();
  }
}
